export type FocusSessionType = "timer" | "stopwatch" | "pomodoro";
export type FocusSessionStatus = "running" | "paused" | "cancelled" | "completed" | "ended";

export type ActiveFocusSessionSummary = {
  id: string;
  type: FocusSessionType;
  status: FocusSessionStatus;
  goalDurationSeconds: number | null;
  elapsedSeconds: number;
  remainingSeconds: number | null;
  startedAt: string;
  endedAt?: string | null;
  moduleKey?: string | null;
  metadata?: Record<string, any> | null;
  syncedAt: number;
};

export type ActivePresenceSummary = {
  sessionId: string;
  moduleKey: string;
  pathname: string;
  startedAt: string;
  activeSeconds: number;
  status: "active";
  syncedAt: number;
};

export const ACTIVE_FOCUS_SESSION_STORAGE_KEY = "activeFocusSessionSummary";
export const ACTIVE_FOCUS_SESSION_EVENT = "activeFocusSessionChanged";
export const ACTIVE_PRESENCE_STORAGE_KEY = "activePresenceSummary";
export const ACTIVE_PRESENCE_EVENT = "activePresenceChanged";

const MODULE_ROUTE_MAP: Array<{ prefix: string; moduleKey: string }> = [
  { prefix: "/dashboard", moduleKey: "dashboard" },
  { prefix: "/navigator", moduleKey: "navigator" },
  { prefix: "/announcements", moduleKey: "announcements" },
  { prefix: "/surveys", moduleKey: "surveys" },
  { prefix: "/employees", moduleKey: "employees" },
  { prefix: "/meetings", moduleKey: "meetings" },
  { prefix: "/calendar", moduleKey: "calendar" },
  { prefix: "/lessons", moduleKey: "lessons" },
  { prefix: "/planning", moduleKey: "planning" },
  { prefix: "/appointments", moduleKey: "appointments" },
  { prefix: "/todos", moduleKey: "todos" },
  { prefix: "/tasks", moduleKey: "tasks" },
  { prefix: "/files", moduleKey: "files" },
  { prefix: "/practice-exams", moduleKey: "practice-exams" },
  { prefix: "/maarif-test-generator", moduleKey: "maarif-test-generator" },
  { prefix: "/maarif-question-wizard", moduleKey: "maarif-question-wizard" },
  { prefix: "/question-bank-indesign", moduleKey: "question-bank-indesign" },
  { prefix: "/whiteboard", moduleKey: "whiteboard" },
  { prefix: "/speed-reading", moduleKey: "speed-reading" },
  { prefix: "/cafe-menu", moduleKey: "cafe-menu" },
  { prefix: "/cafe-campaigns", moduleKey: "cafe-campaigns" },
  { prefix: "/settings", moduleKey: "settings" },
  { prefix: "/time-center", moduleKey: "time-center" },
];

const MODULE_LABEL_MAP: Record<string, string> = {
  dashboard: "Ana Sayfa",
  navigator: "Koçum",
  announcements: "Duyurular",
  surveys: "Anketler",
  employees: "Üyeler",
  meetings: "Toplantılar",
  calendar: "Takvim",
  lessons: "E-Eğitim",
  planning: "Planlama",
  appointments: "Randevular",
  todos: "Yapılacaklar",
  tasks: "Görevler",
  files: "Dosyalar",
  "practice-exams": "Denemeler",
  "maarif-test-generator": "Test Oluşturma",
  "maarif-question-wizard": "Maarif Soru Sihirbazı",
  "question-bank-indesign": "Dizgici",
  whiteboard: "Beyaz Tahta",
  "speed-reading": "Hızlı Okuma",
  "cafe-menu": "Cafe Menüsü",
  "cafe-campaigns": "Cafe Kampanyaları",
  settings: "Sistem Ayarları",
  "time-center": "Zaman Merkezi",
};

export const formatSeconds = (value = 0) => {
  const total = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const getModuleKeyFromPathname = (pathname: string) => {
  if (!pathname || pathname === "/") return "dashboard";
  const match = MODULE_ROUTE_MAP.find((item) => pathname === item.prefix || pathname.startsWith(`${item.prefix}/`));
  return match?.moduleKey || "other";
};

export const getModuleLabel = (moduleKey: string) => {
  return MODULE_LABEL_MAP[moduleKey] || moduleKey;
};

export const readActiveFocusSessionSummary = (): ActiveFocusSessionSummary | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_FOCUS_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const storeActiveFocusSessionSummary = (summary: ActiveFocusSessionSummary | null) => {
  try {
    if (!summary) {
      localStorage.removeItem(ACTIVE_FOCUS_SESSION_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_FOCUS_SESSION_STORAGE_KEY, JSON.stringify(summary));
    }
  } catch {}
  window.dispatchEvent(new CustomEvent(ACTIVE_FOCUS_SESSION_EVENT, { detail: summary }));
};

export const clearActiveFocusSessionSummary = () => {
  storeActiveFocusSessionSummary(null);
};

export const readActivePresenceSummary = (): ActivePresenceSummary | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_PRESENCE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const normalizeActivePresenceSummary = (session: any): ActivePresenceSummary | null => {
  if (!session?.sessionId || !session?.startedAt) return null;
  return {
    sessionId: session.sessionId,
    moduleKey: session.moduleKey || "other",
    pathname: session.pathname || "/",
    startedAt: session.startedAt,
    activeSeconds: Math.max(0, Number(session.activeSeconds) || 0),
    status: "active",
    syncedAt: Date.now(),
  };
};

export const storeActivePresenceSummary = (summary: ActivePresenceSummary | null) => {
  try {
    if (!summary) {
      localStorage.removeItem(ACTIVE_PRESENCE_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_PRESENCE_STORAGE_KEY, JSON.stringify(summary));
    }
  } catch {}
  window.dispatchEvent(new CustomEvent(ACTIVE_PRESENCE_EVENT, { detail: summary }));
};

export const clearActivePresenceSummary = () => {
  storeActivePresenceSummary(null);
};

export const normalizeActiveFocusSessionSummary = (session: any): ActiveFocusSessionSummary | null => {
  if (!session?.id) return null;
  if (!["timer", "stopwatch", "pomodoro"].includes(session.type)) return null;
  if (!["running", "paused", "cancelled", "completed", "ended"].includes(session.status)) return null;
  return {
    id: session.id,
    type: session.type,
    status: session.status,
    goalDurationSeconds: typeof session.goalDurationSeconds === "number" ? session.goalDurationSeconds : null,
    elapsedSeconds: Math.max(0, Number(session.elapsedSeconds) || 0),
    remainingSeconds: session.remainingSeconds === null || session.remainingSeconds === undefined ? null : Math.max(0, Number(session.remainingSeconds) || 0),
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    moduleKey: session.moduleKey || "time-center",
    metadata: session.metadata || {},
    syncedAt: Date.now(),
  };
};

export const getLiveFocusSessionSnapshot = (summary: ActiveFocusSessionSummary | null) => {
  if (!summary) return null;
  const deltaSeconds = summary.status === "running" ? Math.max(0, Math.floor((Date.now() - summary.syncedAt) / 1000)) : 0;
  if (summary.type === "stopwatch") {
    return {
      ...summary,
      liveElapsedSeconds: summary.elapsedSeconds + deltaSeconds,
      liveRemainingSeconds: null,
      isRunning: summary.status === "running",
    };
  }
  return {
    ...summary,
    liveElapsedSeconds: summary.elapsedSeconds + deltaSeconds,
    liveRemainingSeconds: summary.remainingSeconds === null ? null : Math.max(0, summary.remainingSeconds - deltaSeconds),
    isRunning: summary.status === "running",
  };
};

export const getLivePresenceSnapshot = (summary: ActivePresenceSummary | null) => {
  if (!summary) return null;
  const deltaSeconds = Math.max(0, Math.floor((Date.now() - summary.syncedAt) / 1000));
  return {
    ...summary,
    liveActiveSeconds: summary.activeSeconds + deltaSeconds,
    isActive: summary.status === "active",
  };
};
